<script lang="ts">
	import { onMount } from 'svelte';

	import { format } from 'svelte-i18n';

	import { auth_locked_devices } from 'src/stores/device-runtime/local-auth';
	import { updating_devices } from 'src/stores/device-runtime/process';
	import {
		discovered_statuses,
		discovered_settings,
		discovered_info,
	} from 'src/stores/device-runtime/status';

	import { authLockedDeviceModal } from 'src/stores/device-runtime/firmware-ui';
	import { is_gen_2, real_id, get_mode } from 'src/helpers/interface_helpers';
	import { SLEEPING_TYPES, HALF_SLEEPING_TYPES, MODULES } from 'src/constants';
	import { capFirstChar } from 'src/helpers/utilities';

	import {
		get_local_settings,
		get_local_shelly_info,
		get_local_status,
	} from 'src/services/local_requests';

	import {
		AdvancedCard,
		Button,
		Loader,
		Icon,
		Text,
		Modal,
		IconButton,
		Container,
		SelectCard,
		disableWrapperFocus,
		toast,
	} from '@Shelly/shelly-design-system';
	import { devices_config } from '@devices/index';

	import {
		isOwned,
		applyXTDeviceTransform,
		buildDiscoveredDevice,
		submitDeviceForInclusion,
	} from 'src/helpers/inclusion_helpers';
	import type { LocalRequest } from 'src/types/requests/local';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { DeviceInfoG2 } from 'src/types/DeviceInfoG2';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import SharedHomeOwnerManagedModal from 'src/components/SharedHome/SharedHomeOwnerManagedModal.svelte';

	export let inclusionData;
	export let device;

	const gen_2 = is_gen_2(device);
	const imageName = device?.type ? device.type + '.png' : 'unknown-device.svg';
	let image = `images/device_images/${imageName}`;

	// we need to skip the check for the device inclusion process
	let skip_check = true;
	// template data from discovered list
	let title =
			(MODULES.includes(device.type) ? '' : 'Shelly ') +
			devices_config?.[device?.type]?.friendly_name,
		subtitle = { value: device.ip, textSmall: true, bold: false },
		description = { value: device.id, bold: false, textSmall: true };

	// request filled data from 'getDeviceStatusSettings'
	let config;
	let status;
	let settings;
	let info;
	let mode;

	let updating: boolean = false,
		isLoading: boolean = false,
		newStatus: boolean = false;

	$: isAuthLocked = $auth_locked_devices.indexOf(real_id(device.id)) !== -1;
	let isAuthLockedCached = isAuthLocked;

	$: sleepingStatusFailed =
		(SLEEPING_TYPES.includes(device.type) || HALF_SLEEPING_TYPES.includes(device.type)) && !status;

	let showModal = false;
	let showSharedManagedModal = false;

	onMount(() => {
		handleDeviceState();
	});

	$: if (!inclusionData && isAuthLocked !== isAuthLockedCached) {
		handleDeviceState();
		isAuthLockedCached = isAuthLocked;
	}

	//Prepare XT Devices with safeguard against mutation of the device object (preserve transformed device.id)
	$: device && prepareXTDevice($discovered_info[device?.id]);

	const prepareXTDevice = (_info: DeviceInfoG2) => {
		const result = applyXTDeviceTransform(device, _info);
		if (!result) return;

		if (_info?.jti) description.value = device.id;
		if (result.name) title = result.name;
		if (result.image) image = result.image;
	};

	const handleDeviceState = async () => {
		// isAuthLocked = $auth_locked_devices.indexOf(real_id(device.id)) !== -1;
		updating = typeof $updating_devices[device?.id] !== 'undefined';

		let cachedStatus = $discovered_statuses[device.id];

		if (
			!isAuthLocked &&
			!updating &&
			typeof cachedStatus !== 'undefined' &&
			Date.now() < cachedStatus.pulled + 120000
		) {
			status = cachedStatus;
			settings = $discovered_settings[device.id];
			info = $discovered_info[device.id];
			config = devices_config?.[device?.type];
		} else {
			await getDeviceStatusSettings();

			status = $discovered_statuses[device.id];
			settings = $discovered_settings[device.id];
			info = $discovered_info[device.id];

			if (gen_2 && info) {
				device.type = info.model;
			}

			if (!gen_2 && settings && settings.device?.type) {
				device.type = settings.device.type;
			}

			config = devices_config?.[device?.type];

			if (
				typeof config !== 'undefined' &&
				typeof status !== 'undefined' &&
				typeof settings !== 'undefined'
			) {
				mode = get_mode(device, status, settings);
			}
		}
	};

	const handleResponse = (res) => {
		info = res;
		info.pulled = Date.now();
		discovered_info.update((info_list) => {
			return { ...info_list, [device.id]: info };
		});
	};

	const getDeviceStatusSettings = async () => {
		isLoading = true;

		const status_res = await get_local_status(device, device.ip, skip_check);

		if (!status_res.isok) {
			// check if auth lock is enforsed
			isLoading = false;
			if (status_res.status === 401) {
				// isAuthLocked = true;
				isAuthLocked = $auth_locked_devices.indexOf(real_id(device.id)) !== -1;
			}
			// clear the status
			status = undefined;

			discovered_statuses.update((status_list) => {
				delete status_list[device.id];
				return status_list;
			});
			discovered_settings.update((settings_list) => {
				delete settings_list[device.id];
				return settings_list;
			});

			// /shelly info endpoint doesn't require auth — fetch it so XT devices
			// can still resolve jti and display the correct transformed ID
			const infoRes: LocalRequest = await get_local_shelly_info(device, device.ip);
			if (infoRes?.mac) {
				handleResponse(infoRes);
			}
			// }
		} else {
			newStatus = true;
			status = status_res;

			if (!gen_2 && status.update?.status !== 'updating') {
				updating_devices.remove(device.id);
			}

			status.pulled = Date.now();

			discovered_statuses.update((status_list) => {
				return { ...status_list, [device.id]: status };
			});

			const settings_res = await get_local_settings(device, device.ip, skip_check);
			isLoading = false;

			if (!settings_res.isok) {
				settings = undefined;
			} else {
				settings = settings_res;

				settings.pulled = Date.now();
				discovered_settings.update((settings_list) => {
					return { ...settings_list, [device.id]: settings };
				});

				const infoRes: LocalRequest = await get_local_shelly_info(device, device.ip);
				if (infoRes.mac) {
					handleResponse(infoRes);
				} else if (!infoRes?.isok) {
					const reInfoRes: LocalRequest = await get_local_shelly_info(
						{ ...device, gen: 1 },
						device.ip,
					);
					if (reInfoRes.mac) {
						handleResponse(reInfoRes);
					} else if (!reInfoRes?.isok) {
						toast.open({
							type: 'danger',
							title: $format('network_failure_error', {
								default:
									'Network failure: could not get information from device {deviceId}.',
								values: { deviceId: device?.id },
							}),
						});
					}
				}
			}
		}
	};

	const handleAdd = async () => {
		if (isActiveHomeShared($homes.list)) {
			showSharedManagedModal = true;
			return;
		}
		if (info === undefined) {
			toast.open({
				type: 'danger',
				title: $format('network_failure_error', {
					default: 'Network failure: could not get information from device {deviceId}.',
					values: { deviceId: device?.id },
				}),
			});
			return;
		}
		if (!inclusionData) {
			const res = await isOwned(device.id);
			if (res)
				return toast.open({
					type: 'danger',
					title: $format('cloud_connect_owned_err_toast', {
						default: 'The device is already owned by another user\/account.',
					}),
					text: $format('cloud_connect_owned_err_toast_text', {
						values: { deviceId: device.id },
						default:
							'If you believe this is a mistake, please contact our support and provide the device ID: {deviceId}',
					}),
				});
		}

		if (
			typeof config !== 'undefined' &&
			typeof status !== 'undefined' &&
			typeof settings !== 'undefined'
		) {
			mode = get_mode(device, status, settings);
		}

		const _device = buildDiscoveredDevice({ device, image, config, status, settings, mode });

		submitDeviceForInclusion(_device, settings);
	};
</script>

{#if !inclusionData}
	<div use:disableWrapperFocus>
		<AdvancedCard avatar={{ image }} {title} {subtitle} {description} selected={false}>
			<svelte:fragment slot="right">
				{#key status || isAuthLocked || isLoading}
					{#if isLoading}
						<Loader size="m" class="align-self-center" />
					{:else if isAuthLocked}
						<!-- <Icon icon="fal fa-lock " class="text-warning align-self-center mr-s" /> -->
						<IconButton
							size="l"
							variant="danger"
							icon="fal fa-lock"
							class={`text-warning mt-m mb-m mr-s`}
							aria-label={$format('aria_lock_device', { default: 'Lock device' })}
							on:click={(e) => {
								e.stopPropagation();
								authLockedDeviceModal.set(device.id);
							}}
						/>
					{:else if status && settings}
						<Button
							size="m"
							class="align-self-center width-full text-capitalize"
							on:click={handleAdd}
							>{$format('button_add_device', { default: 'add device' })}</Button
						>
					{:else if sleepingStatusFailed}
						<!-- <Tooltip
					placement="top"
					title={$format('discovered_sleeping_tooltip', {
						default:
							'Failed to fully connect to the device, this might be due to the device sleeping to conserve energy. Please use the button to wake it up. '
					})}
				> -->
						<Icon icon="fal fa-circle-info" class="text-warning align-self-center mr-s" />
						<!-- </Tooltip> -->
					{:else if !status || !settings}
						<Icon icon="fal fa-circle-xmark" class="text-danger align-self-center mr-s" />
					{/if}
				{/key}
			</svelte:fragment>
		</AdvancedCard>
	</div>
{:else}
	<SelectCard
		cardBgColor={$showNewLayout ? 'color-1' : undefined}
		class="width-full"
		type="horizontal"
		{image}
		size="s"
		{title}
		descriptiveText={device.id}
		variant={'radio'}
		selected={inclusionData?.selected?.id === device.id}
		on:click={() => {
			inclusionData.selected = device;
			if (!isAuthLocked) handleAdd();
		}}
		disabled={!$discovered_info?.[device?.id] ||
			(!isAuthLocked && (!status || !settings || isLoading || sleepingStatusFailed))}
	/>
{/if}

<Modal
	{showModal}
	title={capFirstChar($format('discovered_failed_label', { default: 'Connection failed' }))}
	on:close={() => (showModal = false)}
>
	<Container slot="content" padding="0">
		<Text class="text-normal">
			{$format('discovered_failed_sleeping_description', {
				default:
					"Failed to fully connect to the Shelly device. Battery powered devices spend most of the time in 'sleep' mode to concerve energy. Please press the button on the device to 'wake it up', and continue its inclusion into your account. ",
			})}
		</Text>
	</Container>
</Modal>

<SharedHomeOwnerManagedModal bind:showModal={showSharedManagedModal} context="add_device" />
